import { Component, Input, Optional, Host, AfterViewInit, OnDestroy, Renderer2 } from '@angular/core';
import { AbstractControlDirective, AbstractControl, FormGroupDirective, FormControlName, UntypedFormGroup } from '@angular/forms';
import { TranslatePipe } from 'translate/translate.pipe';
import { TranslateService } from 'translate/translate.service';
import * as _ from 'underscore';
import { distinctUntilChanged } from 'rxjs/operators';
import { Subscription } from 'rxjs';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'show-errors',
  styles: [`
    .invalid-tooltip {
      position: absolute;
      top: 100%;
      z-index: 5;
      display: none;
      max-width: 100%;
      padding: 0.25rem 0.5rem;
      margin-top: -1.9rem;
      font-size: 0.875rem;
      color: rgba(220, 53, 69, 0.9);
      border-radius: 0.25rem;
      background: transparent;
    }
  `],
  template: `
    <div *ngIf="!inline" class="invalid-tooltip" [style.display]="control?.invalid ? 'unset' : 'none'" [innerHTML]="listOfErrors()">
    </div>
  `,
  standalone: false
})
export class ShowErrorsComponent implements AfterViewInit, OnDestroy {
  private _translatorPipe: TranslatePipe;
  private tooltipInstance: any; // holds the Bootstrap Tooltip instance

  private readonly errorMessages = {
    'required': () =>
      `${this.translatorPipe.transform(this.transform(this.field), '')} ${this.translatorPipe.transform('is required', '')}`,
    'minlength': (params) =>
      `${this.translatorPipe.transform(this.transform(this.field), '')} must be at least ${params.requiredLength} characters long`,
    'maxlength': (params) =>
      `${this.translatorPipe.transform(this.transform(this.field), '')} cannot be more than ${params.requiredLength} characters long`,
    'pattern': (params) =>
      `${this.translatorPipe.transform(this.transform(this.getRequiredPattern(params)), '')}`,
    'notAllowed': () => `Females between the age 10 and 50 are not allowed to book the room`,
    'notSame': () =>
      `${this.translatorPipe.transform(this.transform(this.field), '')} is mismatch`,
    'isBefore': (params) =>
      !!params.message ? params.message : `${this.transform(this.field)} must be greater than From Date`,
    'invalidInvoice': () =>
      `Invalid ${this.translatorPipe.transform(this.transform(this.field), '')}`,
    'isValid': (params) => params.message,
    'isExist': (params) => params.message,
    'isBidderExist': (params) => params.message,
    'alreadyRefunded': () => `Already Refunded`,
  };

  @Input() inline = true;
  @Input() position = 'auto';
  @Input() public control: AbstractControlDirective | AbstractControl;
  @Input() public field: string;
  subscription: Subscription;

  listOfErrors() {
    let error;
    if (this.control.errors) {
      Object.keys(this.control.errors)
        .map(field => error = this.getMessage(field, this.control.errors[field]));
    }
    return error;
  }

  transform(value: string): any {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  ngAfterViewInit() {
    if (this.control instanceof AbstractControl) {
      this.enableTooltip(this.control.invalid);
      this.subscription = this.control.statusChanges.pipe(distinctUntilChanged()).subscribe((status) => {
        if (status === 'VALID') {
          this.enableTooltip(false);
        } else if (status === 'INVALID') {
          this.enableTooltip();
        }
      });
    }
  }

  private getMessage(type: string, params: any) {
    // Unknown validator keys (e.g. Angular's min/max/email or ad-hoc custom
    // errors) used to throw a TypeError inside the template binding, which
    // silently killed the whole error display. Fall back gracefully instead.
    const messageFn = this.errorMessages[type];
    if (messageFn) {
      return messageFn(params);
    }
    if (params && typeof params.message === 'string') {
      return params.message;
    }
    return `${this.translatorPipe.transform(this.transform(this.field || 'Value'), '')} ${this.translatorPipe.transform('is invalid', '')}`;
  }

  private getRequiredPattern(params) {
    if (params.requiredPattern === '^^\\w+([\\.-]?\\w+)*@\\w+([\\.-]?\\w+)*(\\.\\w{2,3})+$$') {
      return `Enter a valid ${this.field} <span class="widget-login-info">(eg:example@example.com)</span>`;
    }
    return `${this.field} not valid`;
  }

  constructor(private ts: TranslateService, private renderer: Renderer2,
    @Optional() @Host() private formGroupDirective: FormGroupDirective) {
  }

  enableTooltip(isEnable = true) {
    let formGroupKey: string | number;
    if (this.control instanceof AbstractControl) {
      const controls = this.formGroupDirective.control.controls;
      const currentFieldkey = _.find(Object.keys(controls), key => {
        if (controls[key] === this.control) {
          return true;
        } else if (controls[key] instanceof UntypedFormGroup) {
          const subControls = (controls[key] as UntypedFormGroup).controls;
          return !!(formGroupKey = _.find(Object.keys(subControls), sKey => subControls[sKey] === this.control));
        }
      });

      let curreFormControName: FormControlName;
      if (controls[currentFieldkey] instanceof UntypedFormGroup) {
        curreFormControName = _.find(this.formGroupDirective.directives, (dir: FormControlName) => dir.name === formGroupKey);
      } else {
        curreFormControName = _.find(this.formGroupDirective.directives, (dir: FormControlName) => dir.name === currentFieldkey);
      }

      if (curreFormControName) {
        const valueAccessor: any = curreFormControName.valueAccessor;
        if (valueAccessor._elementRef) {
          const element: HTMLElement = valueAccessor._elementRef.nativeElement;
          if (isEnable && this.listOfErrors()) {
            const tooltipText = this.listOfErrors() || '';
            const isModal = element.closest('.modal') !== null;
            element.setAttribute('title', tooltipText);
            element.focus();

            if (this.tooltipInstance) {
              this.tooltipInstance.dispose();
            }
            this.tooltipInstance = new (window as any).bootstrap.Tooltip(element, {
              animation: true,
              trigger: 'manual',
              placement: this.position || 'auto',
              title: tooltipText,
              container: isModal ? 'ngb-modal-window' : 'body'
            });
            this.tooltipInstance.show();
            const id = element.getAttribute('aria-describedby');
            if (id) {
              const tooltipEl = document.getElementById(id);
              if (tooltipEl) {
                this.renderer.addClass(tooltipEl, 'error-tooltip');
              }
            }

          } else {
            if (this.tooltipInstance) {
              this.tooltipInstance.hide();
              this.tooltipInstance.dispose();
              this.tooltipInstance = null;
            }
            element.removeAttribute('title');
          }
        }
      }
    }
  }

  ngOnDestroy() {
    this.enableTooltip(false);
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  get translatorPipe(): TranslatePipe {
    if (!this._translatorPipe) {
      this._translatorPipe = new TranslatePipe(this.ts);
    }
    return this._translatorPipe;
  }
}
